import { Box, Button, Stack } from '@mui/material'
import { useEffect } from 'react'
import { FieldError, UseFormSetValue } from 'react-hook-form'
import { DateTimePickerField, IconButton } from 'utoniq-ui'

import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { FullscreenModal } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { tLabel, useForm, UseFormProps, useTranslation } from '@/core'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'
import { convertDatePickerValueToDate, dayjs } from '@/lib/dayjs'

export type ConsolePostReserveProps = {
  open: boolean
  onClose: () => void
  artistId: string
  defaultValue?: string
  onSubmit: (value: PostReserveValues) => Promise<void>
  onDelete?: () => void
}

type PostReserveValues = {
  publishedAt: Date | null
}

const schema: UseFormProps['schema'] = o => ({
  publishedAt: o
    .date()
    .allow(null)
    .optional()
    .label(tLabel('consolePostCreate.publishedAt')),
})

export const ConsolePostReserve: Organism<ConsolePostReserveProps> = ({
  open,
  artistId,
  onClose,
  onSubmit,
  defaultValue,
  onDelete,
}) => {
  const { t } = useTranslation('consolePostCreate')
  const {
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
    setValue,
    reset,
    watch,
  } = useForm<PostReserveValues>({
    schema,
    defaultValues: {
      publishedAt: null,
    },
  })
  const publishedAtWatch = watch('publishedAt')

  useEffect(() => {
    if (open) {
      reset({ publishedAt: defaultValue ?? null })
    }
  }, [defaultValue, open])

  return (
    <OrganismFrame>
      <FullscreenModal
        open={open}
        onClose={() => {
          onClose()
          reset({})
        }}
      >
        <ArtistThemeProvider artistId={artistId}>
          <ConsoleBaseLayout
            header={{
              title: t('title'),
              left: (
                <IconButton.Close
                  edge="start"
                  size="medium"
                  color="white"
                  onClick={() => {
                    onClose()
                    reset({})
                  }}
                />
              ),
            }}
          >
            <Box sx={{ p: 5 }}>
              <PostTextInputCell
                setValue={setValue}
                error={errors.publishedAt}
                value={watch('publishedAt')}
              />
              <Stack alignItems="center" spacing={5}>
                <Button
                  size="large"
                  fullWidth
                  disabled={isSubmitting || !isValid}
                  loading={isSubmitting}
                  onClick={handleSubmit(async values => {
                    await onSubmit(values)
                    onClose()
                  })}
                  sx={{ width: 244 }}
                >
                  {t('set')}
                </Button>
                {onDelete && publishedAtWatch && (
                  <Button
                    variant="text"
                    size="large"
                    fullWidth
                    onClick={() => {
                      onDelete()
                      onClose()
                    }}
                    sx={{ width: 244, color: 'error.main' }}
                  >
                    {t('deleteReserve')}
                  </Button>
                )}
              </Stack>
            </Box>
          </ConsoleBaseLayout>
        </ArtistThemeProvider>
      </FullscreenModal>
    </OrganismFrame>
  )
}

type PostTextInputCellProps = {
  setValue: UseFormSetValue<PostReserveValues>
  value: PostReserveValues['publishedAt']
  error?: FieldError
}
const PostTextInputCell: Cell<PostTextInputCellProps> = ({
  setValue,
  error,
  ...props
}) => {
  const { t } = useTranslation('consolePostCreate')

  return (
    <CellFrame>
      <DateTimePickerField
        onChange={date => {
          setValue('publishedAt', convertDatePickerValueToDate(date), {
            shouldValidate: true,
            shouldDirty: true,
          })
        }}
        error={error}
        value={props.value ? dayjs(props.value) : null}
        title={t('publishedAtLabel')}
        placeholder={t('publishedAtPlaceholder')}
        textFieldProps={{
          label: error ? t('error') : '',
        }}
        titleProps={{
          variant: 'h5',
          color: 'text.primary',
        }}
      />
    </CellFrame>
  )
}
